import { type Context, createContext, useContext, useEffect } from 'react'

export type StepHandler = (() => Promise<void>) | (() => void) | null

export type WizardContextProps = {
  nextStep: () => Promise<void>
  previousStep: () => void
  registerStepHandler: (handler: StepHandler) => void
  skipStep: () => void
  goToStep: (stepName: string) => void
  setCurrentStepCanContinue: (nextCanContinue: boolean) => void
  onStepComplete: () => Promise<void>
  setIsLoading: (nextIsLoading: boolean) => void
  setTitle: (title: string) => void
  setStepSubmittedValues: (stepName: string, values: unknown) => void
  getStepSubmittedValues: <T>(stepName: string) => T | undefined
  setWizardState: (key: string, value: string) => void
  wizardState: Record<string, string>
  meta: {
    title: string
    isLoading: boolean
    steps: string[]
    activeSteps: string[]
  }
  currentStep: {
    isOptional: boolean
    canContinue: boolean
    stepNumber: number
    stepName: string
    hasPreviousStep: boolean
    isPreviouslyCompleted: boolean
  }
}

export const WizardContext: Context<WizardContextProps | null> = createContext<WizardContextProps | null>(null)

export const useWizardContext = (): WizardContextProps => {
  const context = useContext(WizardContext)

  if (!context) {
    throw Error('Steps must be wrapped with `Wizard` component')
  }

  return context
}

export const useSetTitle = (title: string): void => {
  const { setTitle } = useWizardContext()

  useEffect(() => {
    setTitle(title)
  }, [setTitle, title])
}
